import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowUp, ArrowDown, Pencil, Trash2, X, Lock, ImagePlus } from 'lucide-react'
import { adminApi, type CourseCategoryDto } from '@/api/admin'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import ProUpgradeDialog from '@/components/shared/ProUpgradeDialog'
import { FocusedImage, ImageEditor, ImageUploadForm } from './WebsiteImages'
import { categoryErrorMessage } from './categoryErrors'

const MAX_DESCRIPTION = 500

/** A category's picture for the website. Pro only; changes save straight away. */
function CategoryPicture({ category, isPro, onUpgrade }: { category: CourseCategoryDto; isPro: boolean; onUpgrade: () => void }) {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<'view' | 'upload' | 'adjust'>('view')
  const [error, setError] = useState<string | null>(null)
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['course-categories'] })

  const upload = useMutation({
    mutationFn: ({ file, alt }: { file: File; alt: string }) => adminApi.uploadCategoryImage(category.id, file, alt),
    onSuccess: async () => { setMode('view'); setError(null); await refresh() },
    onError: err => setError(categoryErrorMessage(err, 'Could not upload that picture.')),
  })
  const remove = useMutation({
    mutationFn: (id: string) => adminApi.deleteWebsiteImage(id),
    onSuccess: refresh,
    onError: err => setError(categoryErrorMessage(err)),
  })

  const image = category.image
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-700">Picture for your website</span>
        {!isPro && (
          <button type="button" onClick={onUpgrade} className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 hover:text-amber-700 cursor-pointer">
            <Lock size={11} /> Pro
          </button>
        )}
      </div>

      {mode === 'upload' && (
        <ImageUploadForm pending={upload.isPending} error={error} onUpload={(file, alt) => upload.mutate({ file, alt })}
          onCancel={() => { setMode('view'); setError(null) }} />
      )}
      {mode === 'adjust' && image && <ImageEditor image={image} onClose={() => setMode('view')} />}

      {mode === 'view' && (
        <div className="flex items-center gap-3">
          {image ? (
            <FocusedImage image={image} width={480} className="h-14 w-20 shrink-0 rounded-md" />
          ) : (
            <div className="flex h-14 w-20 shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-300"><ImagePlus size={18} /></div>
          )}
          {isPro ? (
            <div className="flex flex-wrap items-center gap-1.5">
              <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => { setMode('upload'); setError(null) }}>
                {image ? 'Replace' : 'Add a picture'}
              </Button>
              {image && (
                <>
                  <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setMode('adjust')}>Adjust</Button>
                  <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={remove.isPending} onClick={() => remove.mutate(image.id)}>Remove</Button>
                </>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-400">Available on the Pro plan</p>
          )}
        </div>
      )}
      {mode === 'view' && error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  )
}

function CategoryRow({
  category, isFirst, isLast, isPro, busy, onMove, onSave, onDelete, onUpgrade,
}: {
  category: CourseCategoryDto
  isFirst: boolean
  isLast: boolean
  isPro: boolean
  busy: boolean
  onMove: (dir: -1 | 1) => void
  onSave: (name: string, description: string | undefined) => Promise<void>
  onDelete: () => Promise<void>
  onUpgrade: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [name, setName] = useState(category.name)
  const [description, setDescription] = useState(category.description ?? '')
  const [error, setError] = useState<string | null>(null)

  const open = () => { setName(category.name); setDescription(category.description ?? ''); setError(null); setEditing(true) }

  const save = async () => {
    setError(null)
    try {
      // Only send the description when it changed, so a Starter plan can still rename.
      const changed = description.trim() !== (category.description ?? '')
      await onSave(name, changed ? description : undefined)
      setEditing(false)
    } catch (err) {
      setError(categoryErrorMessage(err))
    }
  }

  const remove = async () => {
    setError(null)
    try { await onDelete() } catch (err) { setError(categoryErrorMessage(err)); setConfirming(false) }
  }

  return (
    <li className="border border-gray-200 rounded-lg bg-white">
      <div className="flex items-center gap-2 px-3 py-2">
        <div className="flex flex-col">
          <button type="button" disabled={isFirst || busy} onClick={() => onMove(-1)} aria-label={`Move ${category.name} up`}
            className="text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:hover:text-gray-400 cursor-pointer disabled:cursor-default"><ArrowUp size={14} /></button>
          <button type="button" disabled={isLast || busy} onClick={() => onMove(1)} aria-label={`Move ${category.name} down`}
            className="text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:hover:text-gray-400 cursor-pointer disabled:cursor-default"><ArrowDown size={14} /></button>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900 truncate">{category.name}</p>
          <p className="text-xs text-gray-500">
            {category.courseCount === 0 ? 'No courses' : category.courseCount === 1 ? '1 course' : `${category.courseCount} courses`}
            {category.description && ' · has a description'}
            {category.image && ' · has a picture'}
          </p>
        </div>
        {!confirming && (
          <>
            <button type="button" onClick={() => (editing ? setEditing(false) : open())} aria-label={`Edit ${category.name}`}
              className="p-1.5 text-gray-400 hover:text-indigo-600 cursor-pointer"><Pencil size={15} /></button>
            <button type="button" onClick={() => setConfirming(true)} aria-label={`Delete ${category.name}`}
              className="p-1.5 text-gray-400 hover:text-red-600 cursor-pointer"><Trash2 size={15} /></button>
          </>
        )}
      </div>

      {confirming && (
        <div className="px-3 pb-3 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-2">
          <p className="text-sm text-gray-700 flex-1 min-w-[12rem]">
            {category.courseCount > 0
              ? `Delete "${category.name}"? Its ${category.courseCount === 1 ? 'course stays' : `${category.courseCount} courses stay`} but will have no category.`
              : `Delete "${category.name}"?`}
          </p>
          <Button type="button" size="sm" variant="destructive" onClick={remove}>Delete</Button>
          <Button type="button" size="sm" variant="outline" onClick={() => setConfirming(false)}>Keep</Button>
        </div>
      )}

      {editing && !confirming && (
        <div className="px-3 pb-3 space-y-3 border-t border-gray-100 pt-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-700" htmlFor={`cat-name-${category.id}`}>Name</label>
            <Input id={`cat-name-${category.id}`} value={name} maxLength={100} onChange={e => setName(e.target.value)} />
            <p className="text-xs text-gray-500">Renaming keeps the web address <span className="font-mono">{category.slug}</span>.</p>
          </div>
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-gray-700" htmlFor={`cat-desc-${category.id}`}>Description for your website</label>
              {!isPro && (
                <button type="button" onClick={onUpgrade} className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 hover:text-amber-700 cursor-pointer">
                  <Lock size={11} /> Pro
                </button>
              )}
            </div>
            <textarea
              id={`cat-desc-${category.id}`}
              value={description}
              maxLength={MAX_DESCRIPTION}
              disabled={!isPro}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              placeholder={isPro ? 'A sentence or two about this group of courses' : 'Available on the Pro plan'}
              className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:bg-gray-50 disabled:text-gray-400"
            />
            {isPro && <p className="text-xs text-gray-400 text-right">{description.length}/{MAX_DESCRIPTION}</p>}
          </div>
          <CategoryPicture category={category} isPro={isPro} onUpgrade={onUpgrade} />
          <div className="flex justify-end gap-2">
            <Button type="button" size="sm" variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
            <Button type="button" size="sm" onClick={save} disabled={!name.trim()}>Save</Button>
          </div>
        </div>
      )}

      {error && <p className="px-3 pb-2 text-xs text-red-600">{error}</p>}
    </li>
  )
}

/** Add, rename, reorder and delete the tenant's course categories. */
export default function ManageCategoriesDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [newName, setNewName] = useState('')
  const [addError, setAddError] = useState<string | null>(null)
  const [showPro, setShowPro] = useState(false)

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['course-categories'],
    queryFn: () => adminApi.getCourseCategories().then(r => r.data),
    enabled: open,
  })
  const { data: profile } = useQuery({
    queryKey: ['company-profile'],
    queryFn: () => adminApi.getCompanyProfile().then(r => r.data),
    enabled: open,
  })
  const isPro = profile?.planTier === 'Pro'

  // Course lists show the category name, so they need to refresh when a category changes.
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['course-categories'] }),
      queryClient.invalidateQueries({ queryKey: ['courses'] }),
      queryClient.invalidateQueries({ queryKey: ['course'] }),
    ])
  }

  const createMutation = useMutation({
    mutationFn: (name: string) => adminApi.createCourseCategory({ name }),
    onSuccess: async () => { setNewName(''); setAddError(null); await refresh() },
    onError: err => setAddError(categoryErrorMessage(err, 'Could not add the category.')),
  })
  const reorderMutation = useMutation({
    mutationFn: (ids: string[]) => adminApi.reorderCourseCategories(ids),
    onSettled: refresh,
  })

  if (!open) return null

  const move = (index: number, dir: -1 | 1) => {
    const ids = categories.map(c => c.id)
    const target = index + dir
    if (target < 0 || target >= ids.length) return
    ;[ids[index], ids[target]] = [ids[target], ids[index]]
    reorderMutation.mutate(ids)
  }

  const add = () => {
    const name = newName.trim()
    if (!name) { setAddError('Enter a name for the category.'); return }
    createMutation.mutate(name)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div role="dialog" aria-label="Manage categories" className="relative bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="flex items-start justify-between gap-4 p-5 pb-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Course categories</h2>
            <p className="text-sm text-gray-500 mt-0.5">Group your courses. Each course can be in one category.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600 cursor-pointer"><X size={18} /></button>
        </div>

        <div className="px-5 overflow-y-auto flex-1 min-h-[6rem]">
          {isLoading && <p className="text-sm text-gray-500 py-4">Loading…</p>}
          {!isLoading && categories.length === 0 && (
            <p className="text-sm text-gray-500 py-4">No categories yet. Add your first one below.</p>
          )}
          <ul className="space-y-2 pb-2">
            {categories.map((c, i) => (
              <CategoryRow
                key={c.id}
                category={c}
                isFirst={i === 0}
                isLast={i === categories.length - 1}
                isPro={isPro}
                busy={reorderMutation.isPending}
                onMove={dir => move(i, dir)}
                onSave={async (name, description) => {
                  await adminApi.updateCourseCategory(c.id, { name, description })
                  await refresh()
                }}
                onDelete={async () => {
                  await adminApi.deleteCourseCategory(c.id)
                  await refresh()
                }}
                onUpgrade={() => setShowPro(true)}
              />
            ))}
          </ul>
        </div>

        <div className="p-5 pt-3 border-t border-gray-100 space-y-1.5">
          <div className="flex gap-2">
            <Input
              value={newName}
              maxLength={100}
              onChange={e => { setNewName(e.target.value); setAddError(null) }}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add() } }}
              placeholder="New category, e.g. Health & Safety"
              aria-label="New category name"
            />
            <Button type="button" onClick={add} disabled={createMutation.isPending}>{createMutation.isPending ? 'Adding…' : 'Add'}</Button>
          </div>
          {addError && <p className="text-xs text-red-600">{addError}</p>}
        </div>
      </div>

      <ProUpgradeDialog open={showPro} onClose={() => setShowPro(false)} featureName="Category descriptions for your website" />
    </div>
  )
}
